import { PermissionKey, UserPermissionsContext } from '@client/Context/UserPermissions';
import { DropDown, Modal, ModalType, OptionTypeBase } from '@sprint/sprint-react-components';
import _ from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import './BulkEditSingleFieldModal.scss';
import FormBooleanEditor from './FormBooleanEditor';
import FormCheckBoxesEditor from './FormCheckBoxesEditor';
import FormDateEditor from './FormDateEditor';
import FormMultilineTextEditor from './FormMultilineTextEditor';
import FormNumberEditor from './FormNumberEditor';
import FormOwnedByEditor from './FormOwnedByEditor';
import FormRadioButtonsEditor from './FormRadioButtonsEditor';
import FormSelectEditor from './FormSelectEditor';
import FormTagsEditor from './FormTagsEditor';
import FormTextEditor from './FormTextEditor';
import FormWarning from './FormWarning';

export type Field = {
    key: string;
    name: string;
    type: FieldType;
    loadOptions?: (filter: string, loadedOptions: any, page: any) => Promise<any>;
    save: (data: Record<string, any>) => Promise<void>;
    message?: string;
    permissionKey?: PermissionKey;
};
type FieldType =
    | 'number'
    | 'string'
    | 'text-area'
    | 'enum'
    | 'boolean'
    | 'date'
    | 'owner'
    | 'tags'
    | 'checkboxes'
    | 'radio-buttons'
    | 'warning';

export const toFieldType = (value: string): FieldType => {
    switch (value.toLowerCase()) {
        case 'text':
        case 'single-line text':
            return 'string';
        case 'multiline text':
        case 'multi-line text':
        case 'textarea':
            return 'text-area';
        case 'numbers':
        case 'number':
            return 'number';
        case 'dates':
        case 'date':
        case 'date picker':
            return 'date';
        case 'checkboxes':
        case 'multiple checkboxes':
        case 'checkbox':
            return 'checkboxes';
        case 'radio buttons':
        case 'radio':
            return 'radio-buttons';
        case 'pick list':
        case 'dropdown select':
        case 'select':
            return 'enum';
        default:
            throw new Error(`Unknown field type ${value}`);
    }
};

interface Props {
    fields: Field[];
    close: () => void;
}

const BulkEditSingleFieldModal = (props: Props) => {
    const buildComponent = (field: Field) => {
        switch (field.type) {
            case 'string':
                return (
                    <FormTextEditor className={'selected-editor'} value={value} name={field.name} onChange={setValue} />
                );
            case 'number':
                return (
                    <FormNumberEditor
                        className={'selected-editor'}
                        value={value}
                        name={field.name}
                        onChange={setValue}
                    />
                );
            case 'enum':
                return (
                    <FormSelectEditor
                        className={'selected-editor'}
                        value={value}
                        name={field.name}
                        loadOptions={field.loadOptions!}
                        onChange={(selected) => setValue(selected as any)}
                    />
                );
            case 'boolean':
                return (
                    <FormBooleanEditor
                        className={'selected-editor'}
                        value={value}
                        name={field.name}
                        onChange={setValue}
                    />
                );
            case 'date':
                return (
                    <FormDateEditor className={'selected-editor'} value={value} name={field.name} onChange={setValue} />
                );
            case 'owner':
                return (
                    <FormOwnedByEditor
                        className={'selected-editor'}
                        value={value}
                        name={field.name}
                        onChange={setValue}
                    />
                );
            case 'tags':
                return (
                    <FormTagsEditor className={'selected-editor'} value={value} name={field.name} onChange={setValue} />
                );
            case 'text-area':
                return (
                    <FormMultilineTextEditor
                        className={'selected-editor'}
                        value={value}
                        name={field.name}
                        onChange={setValue}
                    />
                );
            case 'checkboxes':
                return (
                    <FormCheckBoxesEditor
                        className={'selected-editor'}
                        value={value}
                        name={field.name}
                        loadOptions={field.loadOptions!}
                        onChange={setValue}
                    />
                );
            case 'radio-buttons':
                return (
                    <FormRadioButtonsEditor
                        className={'selected-editor'}
                        value={value}
                        name={field.name}
                        loadOptions={field.loadOptions!}
                        onChange={setValue}
                    />
                );
            case 'warning':
                return <FormWarning className={'selected-editor'} value={field.message!} name={field.name} />;
        }
    };

    const [option, setOption] = useState<OptionTypeBase>();
    const [editor, setEditor] = useState<React.JSX.Element>();
    const [reloadEditor, setReloadEditor] = useState(false);
    const [value, setValue] = useState<any>();

    const permissions = useContext(UserPermissionsContext);

    useEffect(() => {
        setValue(undefined);
        option && setReloadEditor(true); // this needs to happen after value is set to undefined
    }, [option]);

    useEffect(() => {
        if (reloadEditor) {
            setEditor(buildComponent(option!.value));
            setReloadEditor(false);
        }
    }, [reloadEditor]);

    const save = async () => {
        if (option) {
            const data: Record<string, any> = {};
            data[option.value.key] = value;
            await option.value.save(data);
        }
        return Promise.resolve(true);
    };

    return (
        <Modal
            title={`Bulk Edit`}
            close={props.close}
            isOpen={true}
            type={ModalType.EDIT}
            onSubmit={save}
            className={'js-add-establishments'}
            blocking={true}
        >
            <Form.Group>
                <Form.Label className="match-padding">Select Property</Form.Label>
                <div className="form-group selected-field">
                    <DropDown
                        value={option}
                        isInvalid={false}
                        // Anchor to body to avoid the modal overflow being hidden for dropdown options
                        menuPortalTarget={document.getElementsByTagName('body')[0]}
                        onChange={(selected) => setOption(selected)}
                        options={_.map(
                            props.fields.filter((v) => {
                                return v.permissionKey === undefined || permissions[v.permissionKey].isEnabled;
                            }),
                            (field) => {
                                return {
                                    label: field.name,
                                    value: field,
                                };
                            },
                        )}
                    />
                </div>
            </Form.Group>
            {editor}
        </Modal>
    );
};

export default BulkEditSingleFieldModal;
